import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle, XCircle, AlertTriangle,
  User, MapPin, Activity,
} from 'lucide-react';
import { getApiUrl } from '../services/api.ts';
import { LanguageSwitcher } from '../components/LanguageSwitcher.tsx';
import {
  type ReportLocale,
  mapFetchErrorToLocale,
  reportStrings,
} from './sessionResultsLocales.ts';
import { translateReportRecommendation } from './reportRecommendationTranslations.ts';

interface PostureMetrics {
  overall_score: number;
  neck_angle: number;
  shoulder_alignment: number;
  spine_curvature: number;
  hip_alignment: number;
}

interface SessionData {
  session_id: string;
  device_name?: string;
  location?: string;
  start_time: string;
  end_time: string;
  duration_seconds: number;
  analysis_count: number;
  metrics: PostureMetrics;
  recommendations: string[];
  report_id: string;
  expires_at: string;
}

const SessionResults: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const { i18n } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<SessionData | null>(null);

  const locale: ReportLocale = i18n.language?.startsWith('en') ? 'en' : 'ro';
  const t = reportStrings[locale];

  useEffect(() => {
    if (reportId) {
      fetchSessionResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch only when reportId changes
  }, [reportId]);

  const fetchSessionResults = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(getApiUrl(`/api/reports/public/${reportId}`));

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Report not found or has expired');
        }
        throw new Error('Failed to load report');
      }

      const data = await response.json();
      setSessionData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score: number): string => {
    if (score >= 80) return 'text-regina-success';
    if (score >= 60) return 'text-regina-warning';
    return 'text-regina-error';
  };

  const getScoreBgColor = (score: number): string => {
    if (score >= 80) return 'bg-green-50 border-regina-success';
    if (score >= 60) return 'bg-yellow-50 border-regina-warning';
    return 'bg-red-50 border-regina-error';
  };

  const formatDate = (dateString: string): string => {
    const loc = locale === 'en' ? 'en-US' : 'ro-RO';
    return new Date(dateString).toLocaleString(loc, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getMetricColor = (value: number, metricType: 'shoulder' | 'neck' | 'spine' | 'hip'): string => {
    const absValue = Math.abs(value);

    if (metricType === 'shoulder' || metricType === 'hip') {
      if (absValue <= 3) return '#10B981';
      if (absValue <= 6) return '#F59E0B';
      return '#EF4444';
    }

    if (metricType === 'neck' || metricType === 'spine') {
      if (absValue <= 5) return '#10B981';
      if (absValue <= 10) return '#F59E0B';
      return '#EF4444';
    }

    return '#10B981';
  };

  const errorDisplay = error ? mapFetchErrorToLocale(error, t) : null;

  return (
    <>
      <LanguageSwitcher className="fixed top-3 right-3 z-50" />

      {loading && (
        <div className="min-h-screen bg-regina-gray-50 flex items-center justify-center px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-regina-red mx-auto" />
            <p className="text-regina-gray-700 mt-4 font-medium">{t.loading}</p>
          </div>
        </div>
      )}

      {!loading && (error || !sessionData) && (
        <div className="min-h-screen bg-regina-gray-50 flex items-center justify-center p-4">
          <div className="bg-white border-2 border-regina-error rounded-xl p-6 sm:p-8 max-w-md w-full text-center shadow-lg">
            <XCircle className="w-16 h-16 text-regina-error mx-auto mb-4" />
            <h2 className="text-xl sm:text-2xl font-bold text-regina-gray-800 mb-2">{t.errorTitle}</h2>
            <p className="text-regina-gray-600 mb-6">
              {errorDisplay || t.errorExpiredOrMissing}
            </p>
          </div>
        </div>
      )}

      {!loading && sessionData && (
        <div className="min-h-screen bg-regina-gray-50 py-6 px-4 sm:py-8 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-4 mb-6">
              <div className="bg-white border border-regina-gray-100 rounded-xl shadow-sm p-4 sm:p-6">
                <div className="flex flex-col items-center justify-center text-center py-1">
                  <div className="text-regina-red text-2xl sm:text-3xl font-extrabold tracking-tight">
                    PostureXe
                  </div>
                  <p className="mt-1 text-regina-gray-800 text-sm font-semibold">
                    {t.brandSubtitle}
                  </p>
                </div>
              </div>

              <div className={`border-2 rounded-xl p-6 shadow-sm sm:p-8 ${getScoreBgColor(sessionData.metrics.overall_score)}`}>
                <div className="text-center">
                  <h2 className="text-lg sm:text-xl font-semibold text-regina-gray-700 mb-4">{t.overallScoreTitle}</h2>
                  <div className={`text-5xl sm:text-7xl font-bold ${getScoreColor(sessionData.metrics.overall_score)}`}>
                    {sessionData.metrics.overall_score.toFixed(1)}
                    <span className="text-2xl sm:text-4xl">/100</span>
                  </div>
                  <p className="text-regina-gray-600 mt-4 font-medium">
                    {sessionData.metrics.overall_score >= 80 && t.scoreExcellent}
                    {sessionData.metrics.overall_score >= 60 && sessionData.metrics.overall_score < 80 && t.scoreGood}
                    {sessionData.metrics.overall_score < 60 && t.scoreNeedsWork}
                  </p>
                </div>
              </div>

              <div className="bg-white border border-regina-gray-100 rounded-xl shadow-sm p-4 sm:p-6">
                <div className="text-center mb-6 pb-6 border-b border-regina-gray-100">
                  <h1 className="text-3xl font-bold text-regina-gray-800 mb-3">
                    {t.resultsTitle}
                  </h1>
                  <div className="inline-flex items-center gap-2 text-regina-gray-600 bg-regina-gray-50 px-4 py-2 rounded-lg">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm font-medium">
                      {formatDate(sessionData.end_time)}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-regina-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <Activity className="w-5 h-5 text-regina-red" />
                      <div>
                        <p className="text-regina-gray-600 text-sm">{t.analyses}</p>
                        <p className="text-regina-gray-800 font-medium">{t.framesAnalyzed(sessionData.analysis_count)}</p>
                      </div>
                    </div>
                  </div>

                  {sessionData.location && (
                    <div className="bg-regina-gray-50 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <MapPin className="w-5 h-5 text-regina-success" />
                        <div>
                          <p className="text-regina-gray-600 text-sm">{t.location}</p>
                          <p className="text-regina-gray-800 font-medium">{sessionData.location}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {sessionData.device_name && (
                    <div className="bg-regina-gray-50 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <User className="w-5 h-5 text-regina-red" />
                        <div>
                          <p className="text-regina-gray-600 text-sm">{t.device}</p>
                          <p className="text-regina-gray-800 font-medium">{sessionData.device_name}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mb-6 pb-6 border-b border-regina-gray-100">
                  <h3 className="text-xl font-semibold text-regina-gray-800 mb-6">{t.postureAnalysis}</h3>

                  <div className="grid grid-cols-1 gap-6 sm:gap-8 lg:grid-cols-3">
                    <div className="flex flex-col items-center">
                      <h4 className="text-regina-gray-800 font-medium mb-4">{t.viewFront}</h4>
                      <div className="relative w-full max-w-[16rem] mx-auto sm:mx-0 h-72 sm:h-80 bg-gradient-to-b from-regina-gray-50 to-regina-gray-100 rounded-xl p-4 sm:p-6 border border-regina-gray-200 mb-4">
                        <svg viewBox="0 0 120 180" className="w-full h-full">
                          <ellipse cx="60" cy="18" rx="14" ry="16" fill="#94A3B8" />
                          <ellipse cx="60" cy="20" rx="12" ry="14" fill="#64748B" opacity="0.8" />
                          <ellipse cx="55" cy="38" rx="3" ry="8" fill={getMetricColor(sessionData.metrics.neck_angle, 'neck')} opacity="0.7" />
                          <ellipse cx="65" cy="38" rx="3" ry="8" fill={getMetricColor(sessionData.metrics.neck_angle, 'neck')} opacity="0.7" />
                          <rect x="56" y="32" width="8" height="12" fill={getMetricColor(sessionData.metrics.neck_angle, 'neck')} opacity="0.5" rx="2" />
                          <path d="M 35 50 Q 60 45, 85 50 L 80 60 Q 60 55, 40 60 Z"
                            fill={getMetricColor(sessionData.metrics.shoulder_alignment, 'shoulder')} opacity="0.6" />
                          <ellipse cx="35" cy="58" rx="12" ry="14" fill="#7C8B9F" opacity="0.7" />
                          <ellipse cx="85" cy="58" rx="12" ry="14" fill="#7C8B9F" opacity="0.7" />
                          <line x1="35" y1="50" x2="85" y2="50"
                            stroke={getMetricColor(sessionData.metrics.shoulder_alignment, 'shoulder')}
                            strokeWidth="3" strokeLinecap="round" />
                          <ellipse cx="50" cy="68" rx="10" ry="12" fill="#8B9AAE" opacity="0.5" />
                          <ellipse cx="70" cy="68" rx="10" ry="12" fill="#8B9AAE" opacity="0.5" />
                          <rect x="57" y="50" width="6" height="60"
                            fill={getMetricColor(sessionData.metrics.spine_curvature, 'spine')} opacity="0.7" rx="3" />
                          <ellipse cx="28" cy="75" rx="6" ry="18" fill="#7C8B9F" opacity="0.6" />
                          <ellipse cx="92" cy="75" rx="6" ry="18" fill="#7C8B9F" opacity="0.6" />
                          <ellipse cx="50" cy="112" rx="15" ry="8" fill={getMetricColor(sessionData.metrics.hip_alignment, 'hip')} opacity="0.6" />
                          <ellipse cx="70" cy="112" rx="15" ry="8" fill={getMetricColor(sessionData.metrics.hip_alignment, 'hip')} opacity="0.6" />
                          <line x1="43" y1="112" x2="77" y2="112"
                            stroke={getMetricColor(sessionData.metrics.hip_alignment, 'hip')}
                            strokeWidth="4" strokeLinecap="round" />
                          <ellipse cx="52" cy="135" rx="6" ry="20" fill="#7C8B9F" opacity="0.6" />
                          <ellipse cx="68" cy="135" rx="6" ry="20" fill="#7C8B9F" opacity="0.6" />
                        </svg>
                      </div>
                    </div>

                    <div className="flex flex-col items-center">
                      <h4 className="text-regina-gray-800 font-medium mb-4">{t.viewSide}</h4>
                      <div className="relative w-full max-w-[16rem] mx-auto sm:mx-0 h-72 sm:h-80 bg-gradient-to-b from-regina-gray-50 to-regina-gray-100 rounded-xl p-4 sm:p-6 border border-regina-gray-200 mb-4">
                        <svg viewBox="0 0 120 180" className="w-full h-full">
                          <ellipse cx="65" cy="18" rx="14" ry="16" fill="#94A3B8" />
                          <path d="M 70 18 Q 75 22, 72 28" stroke="#64748B" strokeWidth="2" fill="none" />
                          <path d={`M 62 32 Q ${62 + sessionData.metrics.neck_angle * 0.4} 38, ${60 + sessionData.metrics.neck_angle * 0.3} 48`}
                            fill={getMetricColor(sessionData.metrics.neck_angle, 'neck')} opacity="0.7" stroke="none" />
                          <ellipse cx={60 + sessionData.metrics.neck_angle * 0.2} cy="42" rx="7" ry="12"
                            fill={getMetricColor(sessionData.metrics.neck_angle, 'neck')} opacity="0.6" />
                          <path d={`M ${60 + sessionData.metrics.neck_angle * 0.3} 48 
                           Q ${58 + sessionData.metrics.spine_curvature * 0.35} 70, 
                             ${60 + sessionData.metrics.spine_curvature * 0.25} 90
                           Q ${62 + sessionData.metrics.spine_curvature * 0.15} 100, 60 112`}
                            stroke={getMetricColor(sessionData.metrics.spine_curvature, 'spine')}
                            strokeWidth="5" fill="none" strokeLinecap="round" />
                          <ellipse cx="70" cy="68" rx="18" ry="14" fill="#8B9AAE" opacity="0.5" />
                          <ellipse cx="52" cy="75" rx="10" ry="25" fill="#7C8B9F" opacity="0.6" />
                          <ellipse cx="60" cy="55" rx="14" ry="12" fill="#7C8B9F" opacity="0.7" />
                          <ellipse cx="58" cy="115" rx="14" ry="12" fill="#7C8B9F" opacity="0.6" />
                          <ellipse cx="60" cy="112" rx="10" ry="8"
                            fill={getMetricColor(sessionData.metrics.hip_alignment, 'hip')} opacity="0.7" />
                          <ellipse cx="62" cy="140" rx="9" ry="22" fill="#7C8B9F" opacity="0.6" />
                          <ellipse cx="60" cy="168" rx="7" ry="12" fill="#6B7A8E" opacity="0.5" />
                        </svg>
                      </div>
                    </div>

                    <div className="flex flex-col items-center">
                      <h4 className="text-regina-gray-800 font-medium mb-4">{t.viewBack}</h4>
                      <div className="relative w-full max-w-[16rem] mx-auto sm:mx-0 h-72 sm:h-80 bg-gradient-to-b from-regina-gray-50 to-regina-gray-100 rounded-xl p-4 sm:p-6 border border-regina-gray-200 mb-4">
                        <svg viewBox="0 0 120 180" className="w-full h-full">
                          <ellipse cx="60" cy="18" rx="14" ry="16" fill="#94A3B8" />
                          <ellipse cx="60" cy="20" rx="11" ry="13" fill="#64748B" opacity="0.6" />
                          <rect x="55" y="28" width="10" height="16" fill={getMetricColor(sessionData.metrics.neck_angle, 'neck')} opacity="0.7" rx="3" />
                          <path d="M 30 48 Q 60 42, 90 48 L 85 65 Q 60 58, 35 65 Z"
                            fill={getMetricColor(sessionData.metrics.shoulder_alignment, 'shoulder')} opacity="0.7" />
                          <line x1="32" y1="52" x2="88" y2="52"
                            stroke={getMetricColor(sessionData.metrics.shoulder_alignment, 'shoulder')}
                            strokeWidth="4" strokeLinecap="round" />
                          <ellipse cx="32" cy="58" rx="13" ry="15" fill="#7C8B9F" opacity="0.7" />
                          <ellipse cx="88" cy="58" rx="13" ry="15" fill="#7C8B9F" opacity="0.7" />
                          <path d="M 42 55 L 42 75 L 50 70 L 48 58 Z" fill="#5A6B7F" opacity="0.7" stroke="#4A5B6F" strokeWidth="1" />
                          <path d="M 78 55 L 78 75 L 70 70 L 72 58 Z" fill="#5A6B7F" opacity="0.7" stroke="#4A5B6F" strokeWidth="1" />
                          <ellipse cx="54" cy="75" rx="4" ry="30" fill={getMetricColor(sessionData.metrics.spine_curvature, 'spine')} opacity="0.6" />
                          <ellipse cx="66" cy="75" rx="4" ry="30" fill={getMetricColor(sessionData.metrics.spine_curvature, 'spine')} opacity="0.6" />
                          <rect x="57" y="50" width="6" height="60" fill={getMetricColor(sessionData.metrics.spine_curvature, 'spine')} opacity="0.8" rx="3" />
                          <path d="M 40 80 Q 50 85, 60 88 Q 70 85, 80 80 L 75 100 Q 60 105, 45 100 Z"
                            fill="#6B7A8E" opacity="0.6" />
                          <ellipse cx="52" cy="118" rx="14" ry="12" fill="#7C8B9F" opacity="0.7" />
                          <ellipse cx="68" cy="118" rx="14" ry="12" fill="#7C8B9F" opacity="0.7" />
                          <line x1="42" y1="115" x2="78" y2="115"
                            stroke={getMetricColor(sessionData.metrics.hip_alignment, 'hip')}
                            strokeWidth="4" strokeLinecap="round" />
                          <ellipse cx="52" cy="142" rx="7" ry="22" fill="#7C8B9F" opacity="0.6" />
                          <ellipse cx="68" cy="142" rx="7" ry="22" fill="#7C8B9F" opacity="0.6" />
                          <ellipse cx="52" cy="168" rx="6" ry="12" fill="#6B7A8E" opacity="0.6" />
                          <ellipse cx="68" cy="168" rx="6" ry="12" fill="#6B7A8E" opacity="0.6" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-regina-gray-100">
                    <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 rounded-full bg-green-500" />
                        <span className="text-regina-gray-700">{t.legendOptimal}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 rounded-full bg-yellow-500" />
                        <span className="text-regina-gray-700">{t.legendMinor}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 rounded-full bg-red-500" />
                        <span className="text-regina-gray-700">{t.legendNeedsCorrection}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-6 pb-6 border-b border-regina-gray-100">
                  <h3 className="text-xl font-semibold text-regina-gray-800 mb-4">{t.detailedMetrics}</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <MetricRow
                      label={t.neckAngle}
                      value={sessionData.metrics.neck_angle}
                      metricType="neck"
                      unit="°"
                      idealRangeTitle={t.idealRange}
                    />
                    <MetricRow
                      label={t.shoulderAlignment}
                      value={sessionData.metrics.shoulder_alignment}
                      metricType="shoulder"
                      unit="°"
                      idealRangeTitle={t.idealRange}
                    />
                    <MetricRow
                      label={t.spineCurvature}
                      value={sessionData.metrics.spine_curvature}
                      metricType="spine"
                      unit="°"
                      idealRangeTitle={t.idealRange}
                    />
                    <MetricRow
                      label={t.spineAlignment}
                      value={sessionData.metrics.spine_curvature}
                      metricType="spine"
                      unit="°"
                      idealRangeTitle={t.idealRange}
                    />
                    <MetricRow
                      label={t.hipAlignment}
                      value={sessionData.metrics.hip_alignment}
                      metricType="hip"
                      unit="°"
                      idealRangeTitle={t.idealRange}
                    />
                    <MetricRow
                      label={t.shoulderBalance}
                      value={sessionData.metrics.shoulder_alignment}
                      metricType="shoulder"
                      unit="°"
                      idealRangeTitle={t.idealRange}
                    />
                  </div>
                </div>

                {sessionData.recommendations && sessionData.recommendations.length > 0 && (
                  <div>
                    <h3 className="text-xl font-semibold text-regina-gray-800 mb-4 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-yellow-400" />
                      {t.recommendations}
                    </h3>
                    <ul className="space-y-3 mb-6">
                      {sessionData.recommendations.map((rec, index) => (
                        <li key={index} className="flex items-start gap-3 text-regina-gray-700">
                          <span className="text-regina-gray-800 text-xl font-bold leading-none">•</span>
                          <span className="flex-1">
                            {translateReportRecommendation(rec, locale)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <p className="text-regina-gray-500 text-sm text-center mt-6">
              {t.reportExpiresOn} {formatDate(sessionData.expires_at)}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

interface MetricRowProps {
  label: string;
  value: number;
  metricType: 'shoulder' | 'neck' | 'spine' | 'hip';
  unit: string;
  idealRangeTitle: string;
}

const MetricRow: React.FC<MetricRowProps> = ({ label, value, metricType, unit, idealRangeTitle }) => {
  const absValue = Math.abs(value);

  let isGood: boolean;
  let isModerate: boolean;
  let idealRange: string;

  if (metricType === 'shoulder' || metricType === 'hip') {
    isGood = absValue <= 3;
    isModerate = absValue > 3 && absValue <= 6;
    idealRange = '0° ± 3°';
  } else {
    isGood = absValue <= 5;
    isModerate = absValue > 5 && absValue <= 10;
    idealRange = '0° ± 5°';
  }

  return (
    <div className="flex items-center justify-between bg-regina-gray-50 rounded-lg p-4 border border-regina-gray-200">
      <div className="flex-1">
        <p className="text-regina-gray-800 font-semibold">{label}</p>
        <p className="text-regina-gray-500 text-sm">{idealRangeTitle}: {idealRange}</p>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-regina-gray-800 font-bold text-xl">
          {value.toFixed(1)}{unit}
        </span>
        {isGood ? (
          <CheckCircle className="w-6 h-6 text-green-500" />
        ) : isModerate ? (
          <AlertTriangle className="w-6 h-6 text-yellow-500" />
        ) : (
          <XCircle className="w-6 h-6 text-red-500" />
        )}
      </div>
    </div>
  );
};

export default SessionResults;
